import { ButtonWrapper, MatButton } from 'components/buttons'
import { MicLightIconButton } from 'components/icon-buttons/MicLightIconButton'
import { VideoLightIconButton } from 'components/icon-buttons/VideoLightIconButton'
import { LogoContainer } from 'pages/logoContainer/LogoContainer'
import { useContext, useEffect, useState, useRef } from 'react'
import { BookingContext } from 'contexts/booking'
import { getDisplayName, getSameOriginPhotoUrl } from 'utils/get-display-name'
import { MessageDialog } from 'components/dialog/MessageDialog'
import { IOSPermissionMessageDialog } from 'components/dialog/ios-permission-messageDialog'
import ZoomVideo, { LocalVideoTrack, MediaDevice } from '@zoom/videosdk'
import { useNavigate } from 'react-router-dom'
import { SessionWrapper } from 'components/wrappers'
import { useMediaDevices } from './useMediaDevices'
import { useTimer } from 'components/timer/useTimer'
import { Indicator } from 'components/timer/Indicator'
import { useNavigateBackDisabled } from 'pages/useNavigateBackDisabled'
import { useFeatureCheck } from 'hooks/useFeatureCheck'
import { SettingDialog } from 'pages/Diagnostics/SettingDialog'
import { rollbarService, RollbarExceptionNames } from 'services/rollbar'
import {
    AlertContent,
    Avatar,
    ButtonContainer,
    ButtonMessage,
    IconButtonContainer,
    IconsContainer,
    InnerWrapper,
    MessageRemainingTimeWrapper,
    ParticipantInitials,
    PrivacyPolicy,
    Separator,
    Tile,
    TileWrapper,
    PopoverWrapper,
    CustomPopoverWrapper
} from './styles'
import { AlertTitle, Badge, Typography } from '@mui/material'
import { Icon } from 'hello-design-system'
import { SettingIconButton, VideoBackgroundIconButton } from 'components/icon-buttons'
import { AlertDialogContent } from 'utils/constants'
import { VBDialog } from './components/virtual-background/VirtualBackground'

const client = ZoomVideo.createClient()
const localAudioTrack = ZoomVideo.createLocalAudioTrack()
const { StudentInfo, TeacherTitle, DiagnosticWarningDescription, DiagnosticWarningTitle } = AlertDialogContent

type DiagnosticsPageProperties = {
    startTime?: number | undefined
}
type ChooseBackgroundType = { active: boolean; url: string; change: string }

const DiagnosticsPage: React.FC<DiagnosticsPageProperties> = ({ startTime }) => {
    const { devices: mediaDevices } = useMediaDevices()
    const booking = useContext(BookingContext)
    const { isMobileSafari } = useFeatureCheck()
    useNavigateBackDisabled()
    const [isMessageDialogOpen, setIsMessageDialogOpen] = useState(false)
    const [isMicActive, setIsMicActive] = useState(false)
    const [isCamActive, setIsCamActive] = useState(false)
    const [permissionType, setPermissionType] = useState<'microphone' | 'camera'>('microphone')
    const [videoDevices, setVideoDevices] = useState<MediaDevice[]>([])
    const [audioDevices, setAudioDevices] = useState<MediaDevice[]>([])
    const [isMicAlertEnabled, setIsMicAlertEnabled] = useState(false)
    const [isCamAlertEnabled, setIsCamAlertEnabled] = useState(false)
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
    const navigate = useNavigate()
    const isAudioAlreadyStarted = useRef(false)
    const callbackUrl = sessionStorage.getItem('callbackUrl')
    // when callback url is from Speak+(D2C) application then will navigate to its privacy policy page
    // otherwise it will navigate to core course privacy policy page
    const privacyPolicyUrl = callbackUrl?.includes('speak') ? `${callbackUrl}/privacy` : `${callbackUrl}/privacy-policy`
    const waitingTime = booking.status === 'validated' && booking.check.session.waitTime / 60
    const [remainingTime] = useTimer(startTime, +waitingTime)
    const [isSettingActive, setSettingsActive] = useState<boolean>(false)
    const [selectedCameraId, setSelectedCameraId] = useState<string>('')
    const featureFlag = process.env.REACT_APP_ENV === 'local'
    const isStudent = booking.status === 'validated' && booking.user.role === 'student'
    const isVbPromo = window.growthbook ? window.growthbook.isOn('goc-virtual-background-promo') : false
    const localVideoTrackRef = useRef<LocalVideoTrack | null>(null)
    const isMicUnmuted = useRef(false)

    const [backgroundSelected, setBackgroundSelected] = useState<ChooseBackgroundType>({ active: false, url: 'blur', change: 'blur' })

    useEffect(() => {
        const videoButton = document.getElementById('video-background-icon-button')
        if (videoButton) {
            setAnchorEl(videoButton)
        }
    }, [])

    useEffect(() => {
        // Clean up the stream when the component unmounts
        return () => {
            // On component unmount, stopping the audio & video
            isAudioAlreadyStarted.current && void localAudioTrack.stop()
            void stopPreviewVideoButton()
        }
    }, [])

    const VbButton = (
        <VideoBackgroundIconButton
            active={backgroundSelected.url.length > 0}
            alert={isCamAlertEnabled}
            onClick={() => {
                onCamClickHandler()
                setBackgroundSelected({ ...backgroundSelected, active: true })
                setAnchorEl(null)
            }}
        />
    )

    // This effect runs whenever the selected camera changes
    const updateCameraStream = async (): Promise<void> => {
        try {
            if (localVideoTrackRef.current) {
                await localVideoTrackRef.current.switchCamera(selectedCameraId)
            }
        } catch (error) {
            console.error('Error updating camera stream:', error)
        }
    }

    useEffect(() => {
        if (selectedCameraId) {
            void updateCameraStream()
        }
    }, [selectedCameraId])

    const fetchBackgrounds = async (): Promise<void> => {
        if (!process.env.REACT_APP_VB_API_URL || sessionStorage.getItem('TeacherBackground')) return
        const apiUrl = process.env.REACT_APP_VB_API_URL
        const token = process.env.REACT_APP_VB_ACCESS_TOKEN

        if (sessionStorage.getItem('teacherBackground')) return

        try {
            const response = await fetch(apiUrl, {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            })
            if (!response.ok) {
                throw new Error('Failed to fetch data')
            }
            const vbData = await response.json()
            if (booking.status === 'validated') {
                if (booking.user.role === 'teacher') {
                    const bgList = (vbData.data[0].attributes.content.teacher[0].virtualBackgroundList)
                    sessionStorage.setItem('teacherBackground', bgList[0]?.imageUrl || '')
                    sessionStorage.setItem('selectedBackgroundId', bgList[0]?.imageName || '')
                } else {
                    sessionStorage.setItem('studentVirtualBackgroundList', JSON.stringify(vbData.data[0].attributes.content.student))
                    sessionStorage.setItem('studentSelectedBackground', 'blur')
                    sessionStorage.setItem('selectedBackgroundId', 'blur')
                }
                sessionStorage.setItem('toggleVirtualBackground', 'true')
            }
        } catch (error) {
            console.error('Error fetching backgrounds:', error)
        }
    }

    useEffect(() => {
        if (booking.status !== 'validated') return

        const enableVirtualBackground = booking.user.role === 'teacher'
            ? 'goc-virtual-background-teacher'
            : 'goc-virtual-background'

        if (window.growthbook.isOn(enableVirtualBackground)) {
            void fetchBackgrounds()
        }
    }, [booking])

    const toggleMessageDialog = (): void => {
        setIsMessageDialogOpen(prevState => !prevState)
    }

    const onMicClickHandler = (): void => {
        if (audioDevices.length && !isMicAlertEnabled) {
            isMicActive ? void stopPreviewMicrophoneButton() : void startPreviewMicrophoneButton()
            setIsMicActive(prevState => !prevState)
        } else {
            setIsMicAlertEnabled(true)
            setPermissionType('microphone')
            toggleMessageDialog()
        }
    }

    const onCamClickHandler = (): void => {
        if (videoDevices.length && !isCamAlertEnabled) {
            isCamActive ? void stopPreviewVideoButton() : void startPreviewVideoButton(selectedCameraId)
            setIsCamActive(prevState => !prevState)
        } else {
            setIsCamAlertEnabled(true)
            setPermissionType('camera')
            toggleMessageDialog()
        }
    }
    const navigateToClassroom = (isDiagnostic: boolean): void => {
        const status = {
            isDiagnosed: true,
            isVideoMuted: !isCamActive,
            isAudioMuted: !isMicActive,
            isVideoDisabled: videoDevices.length === 0 && isCamAlertEnabled,
            selectedCamera: selectedCameraId
        }
        sessionStorage.setItem('diagnoseStatus', JSON.stringify(status))
        if (booking.status === 'validated' && booking.user.role === 'teacher') {
            navigate('/teacher/classroom', { state: { isDiagnostic } })
        } else {
            navigate('/classroom', { state: { isDiagnostic } })
        }
    }

    const mediaDeviceFilter = (kind: string): MediaDeviceInfo[] => {
        return mediaDevices?.filter((device: MediaDeviceInfo) => device?.kind === kind && device?.deviceId !== '') || []
    }

    const joinClass = (): void => {

        // check is audio permission is enabled if not show dialog
        const audioFilteredData = mediaDeviceFilter('audioinput')
        logRollbar(RollbarExceptionNames.DiagnosticJoinButtonClick)

        if (isMicAlertEnabled && !audioFilteredData.length) {
            setPermissionType('microphone')
            !isMicActive
                ? toggleMessageDialog()
                : logRollbarOnError(RollbarExceptionNames.DiagnosticJoinMicError)
        } else {
            if (booking.status === 'validated') {
                // Ensure the camera stream is updated with the selected camera before navigating
                if (selectedCameraId) {
                    void updateCameraStream().then(() => {
                        navigateToClassroom(true)
                    }).catch(e => {
                        logRollbarOnError(RollbarExceptionNames.DiagnosticJoinButtonVideoStreamError, JSON.stringify(e))
                    })
                } else {
                    // If no camera is selected, proceed with the existing logic
                    showPreview()
                    logRollbar(RollbarExceptionNames.DiagnosticNoIssue)
                    navigateToClassroom(true)
                }
            } else {
                logRollbarOnError(RollbarExceptionNames.DiagnosticJoinButtonStatusError)
            }
        }
    }

    const logRollbarOnError = (rollbarName: RollbarExceptionNames, error?: any, optionalData?: object): void => {
        let filteredVideoDevices: MediaDeviceInfo[] = []
        let filteredAudioDevices: MediaDeviceInfo[] = []

        if (mediaDevices && Array.isArray(mediaDevices) && mediaDevices.length > 0) {
            filteredVideoDevices = mediaDeviceFilter('videoinput')
            filteredAudioDevices = mediaDeviceFilter('audioinput')
        }

        rollbarService.rollbar.error(
            rollbarName,
            {
                userId: booking.status === 'validated' ? booking.user.id : '',
                userName: booking.status === 'validated' ? booking.user.firstName : '',
                classRoomId: booking.status === 'validated' ? booking.classroomId : '',
                bookingStatus: booking.status,
                filteredVideoDevices,
                filteredAudioDevices,
                isMicActive,
                isMicAlertEnabled,
                isCamActive,
                isCamAlertEnabled,
                error,
                ...optionalData
            }
        )
    }

    const logRollbar = (rollbarName: RollbarExceptionNames): void => {
        rollbarService.rollbar.log(rollbarName, {
            userId: booking.status === 'validated' ? booking.user.id : '',
            selectedCamera: selectedCameraId,
            bookingStatus: booking.status
        })
    }

    useEffect(() => {
        if (mediaDevices) {
            showPreview()
        } else {
            void startPreviewVideoButton(selectedCameraId)
            checkAndStartOrStopTheDevices(void 0)
        }
    }, [mediaDevices, selectedCameraId])

    const showPreview = (): void => {
        let videoDevicesList: MediaDevice[] = []
        let audioDevicesList: MediaDevice[] = []

        videoDevicesList = mediaDeviceFilter('videoinput')
        audioDevicesList = mediaDeviceFilter('audioinput')

        setVideoDevices(videoDevicesList)
        setAudioDevices(audioDevicesList)

        if (videoDevicesList.length) {
            setSelectedCameraId(videoDevicesList[0].deviceId)
        }

        startPreviewVideoButton(selectedCameraId).then(value => {
            checkAndStartOrStopTheDevices(void 0)
        }).catch(err => {
            logRollbarOnError(
                RollbarExceptionNames.DiagnosticMediaDeviceNavigatorError,
                JSON.stringify(err),
                { err, errMessage: err.message, videoDevicesList, audioDevicesList }
            )
            console.log(err, err.message, videoDevicesList, audioDevicesList, 'Error - navigator.mediaDevices.getUserMedia()')
            checkAndStartOrStopTheDevices(void 0, err.message)
        })
    }
    const handleDeviceChange = (): void => {
        const filteredVideoDevices = mediaDeviceFilter('videoinput')
        const isSelectedCameraAvailable = filteredVideoDevices.some((device: MediaDeviceInfo) => device.deviceId === selectedCameraId)

        if (!isSelectedCameraAvailable && filteredVideoDevices.length) {
            setIsCamActive(true)
            setSelectedCameraId(filteredVideoDevices[0].deviceId)
            void updateCameraStream()
        } else if (!filteredVideoDevices.length) {
            setIsCamActive(false)
        }
        filteredVideoDevices.length && handleCameraSelection(filteredVideoDevices[0].deviceId)
    }
    navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange)

    const checkAndStartOrStopTheDevices = (isPermissionDenied: boolean = false, errMessage: string = ''): void => {
        const videoDevices = mediaDeviceFilter('videoinput')
        const audioDevices = mediaDeviceFilter('audioinput')
        if (errMessage === 'Permission denied by system') {
            isPermissionDenied && stopPreviewMicrophoneButton()
            isPermissionDenied && stopPreviewVideoButton()
            setIsCamActive(false)
            setIsMicActive(false)
            setIsMicAlertEnabled(!audioDevices.length)
            setIsCamAlertEnabled(!videoDevices.length)
        } else {
            // for audio devices
            if (audioDevices.length) {
                if (!isAudioAlreadyStarted.current) {
                    void localAudioTrack.start().then(() => {
                        isAudioAlreadyStarted.current = true
                        void startPreviewMicrophoneButton()
                        setIsMicActive(true)
                    })
                } else {
                    void startPreviewMicrophoneButton()
                    setIsMicActive(true)
                }
                setIsMicAlertEnabled(false)
            } else {
                isPermissionDenied && stopPreviewMicrophoneButton()
                setIsMicActive(false)
                setIsMicAlertEnabled(true)
            }
            // for video devices
            if (videoDevices.length) {
                startPreviewVideoButton(selectedCameraId).catch(error => {
                    console.log('the camera is not streaming in diagnostic:', error)
                    logRollbarOnError(RollbarExceptionNames.DiagnosticCameraNotStreaming, error)
                })
                setIsCamActive(true)
                setIsCamAlertEnabled(false)
            } else {
                isPermissionDenied && stopPreviewVideoButton()
                setIsCamActive(false)
                setIsCamAlertEnabled(true)
            }
        }
    }

    const startPreviewVideoButton = async (selectedCameraId: string): Promise<void> => {
        try {
            const videoElement = document.getElementById('render-diagnostic-video-preview') as HTMLVideoElement
            if (!videoElement) return

            videoElement.style.transform = 'scaleX(-1)'
            await new Promise(requestAnimationFrame)

            try {
                // Initialize Zoom client if not already initialized
                await client.init('en-US', 'CDN')
            } catch (e: any) {
                // Ignore "already initialized" errors when navigating back/forth
            }

            if (localVideoTrackRef.current) {
                await localVideoTrackRef.current.stop()
                localVideoTrackRef.current = null
            }
            if (!selectedCameraId) {
                return
            }

            try {
                localVideoTrackRef.current = ZoomVideo.createLocalVideoTrack(selectedCameraId)
                if (!localVideoTrackRef.current) throw new Error('Failed to create video track.')

                let prevStoreUrl: string = ''
                if (booking.status === 'validated') {
                    prevStoreUrl = sessionStorage.getItem(booking.user.role === 'teacher' ? 'teacherBackground' : 'studentSelectedBackground') || ''
                }

                if (prevStoreUrl === 'blur') {
                    await localVideoTrackRef.current.start(videoElement)
                    await localVideoTrackRef.current.updateVirtualBackground('blur')
                } else if (prevStoreUrl) {
                    try {
                        await localVideoTrackRef.current.start(videoElement, { imageUrl: prevStoreUrl })
                    } catch (e) {
                        console.error('DiagnosticsPage DEBUG: Error starting with background image, falling back to standard start', e)
                        await localVideoTrackRef.current.start(videoElement)
                        await localVideoTrackRef.current.updateVirtualBackground(prevStoreUrl)
                    }
                } else {
                    await localVideoTrackRef.current.start(videoElement)
                }
            } catch (error) {
                console.error('Error creating local video track catch block:', error)
            }
            setIsCamActive(true)
        } catch (error) {
            logRollbarOnError(RollbarExceptionNames.DiagnosticStartPreviewVideoError, JSON.stringify(error))
        }
    }

    const stopPreviewVideoButton = async (): Promise<void> => {
        try {
            if (localVideoTrackRef.current) {
                await localVideoTrackRef.current.stop()
                localVideoTrackRef.current = null
            }
            console.log('Camera stopped successfully.')
        } catch (error) {
            logRollbarOnError(RollbarExceptionNames.DiagnosticStopPreviewVideoError, JSON.stringify(error))
        }
    }

    const startPreviewMicrophoneButton = async (): Promise<void> => {
        if (isMicUnmuted.current) {
            return
        }

        try {
            await localAudioTrack.unmute()
            isMicUnmuted.current = true
        } catch (error: any) {
            if (error?.name === 'AudioAlreadyUnmutedError') {
                console.warn('Microphone is already unmuted.')
                isMicUnmuted.current = true
            } else {
                logRollbarOnError(RollbarExceptionNames.DiagnosticAudioUnmuteError, error)
            }
        }
    }

    const stopPreviewMicrophoneButton = async (): Promise<void> => {
        try {
            await localAudioTrack.mute()
            isMicUnmuted.current = false
        } catch (error) {
            logRollbarOnError(RollbarExceptionNames.DiagnosticAudioMuteError, error)
        }
    }

    const toggleSettingsModal = (): void => {
        setSettingsActive(prevState => !prevState)
    }

    const handleCameraSelection = (CameraId: string): void => {
        setSelectedCameraId(CameraId)
    }

    return (
        <SessionWrapper>
            <LogoContainer>
                <InnerWrapper>
                    {booking.status === 'validated' ? (
                        <>
                            <TileWrapper>
                                <Tile showVideo={isCamActive}>
                                    <video-player-container
                                        id='video-player-container-diagnostic'
                                        style={{
                                            position: 'relative',
                                            width: '100%',
                                            height: '100%',
                                            overflow: 'hidden',
                                            display: isCamActive ? 'block' : 'none'
                                        }}
                                    >
                                        <video-player
                                            id='render-diagnostic-video-preview'
                                            style={{
                                                position: 'absolute',
                                                inset: 0,
                                                display: isCamActive ? 'block' : 'none',
                                                objectFit: 'cover',
                                                willChange: 'transform'
                                            }}
                                        />
                                    </video-player-container>

                                    {!isCamActive
                                        ? <Avatar id='participant-avatar'
                                            sx={{ backgroundImage: booking.user.photoUri ? `url('${getSameOriginPhotoUrl(booking.user.photoUri)}')` : 'none' }}>
                                            {!booking.user.photoUri ? (
                                                <ParticipantInitials id='participant-initials'>
                                                    {getDisplayName(booking.user, 'initials')}
                                                </ParticipantInitials>
                                            ) : null}
                                        </Avatar> : null}
                                </Tile>
                            </TileWrapper>
                            <ButtonContainer>
                                <AlertContent
                                    title={isMicAlertEnabled ? DiagnosticWarningTitle : isStudent ? '' : TeacherTitle}
                                    description={isMicAlertEnabled ? DiagnosticWarningDescription : isStudent ? StudentInfo : ''}
                                    severity={isMicAlertEnabled ? 'warning' : 'info'}
                                    variant='outlined'
                                    warning={isMicAlertEnabled}
                                    action={isMicAlertEnabled
                                        ? (<MatButton
                                            id='warning'
                                            variant='contained'
                                            color='warning'
                                            onClick={onMicClickHandler}
                                            className='warning-action'
                                            size='small'
                                            width='203px'
                                            height='30px'
                                            borderRadius='8px'
                                        >
                                            <ButtonMessage>
                                                How do I enable access?
                                            </ButtonMessage>
                                        </MatButton>)
                                        : undefined
                                    }
                                >
                                    <AlertTitle>
                                        {isMicAlertEnabled ? DiagnosticWarningTitle : isStudent ? '' : TeacherTitle}
                                    </AlertTitle>
                                    {isMicAlertEnabled ? DiagnosticWarningDescription : isStudent ? StudentInfo : ''}
                                </AlertContent>
                                <IconButtonContainer>
                                    <ButtonWrapper>
                                        <MicLightIconButton
                                            active={isMicActive}
                                            alert={isMicAlertEnabled}
                                            onClick={onMicClickHandler}
                                        />
                                        <IconsContainer>
                                            <VideoLightIconButton
                                                active={isCamActive}
                                                alert={isCamAlertEnabled}
                                                onClick={onCamClickHandler}
                                            />
                                        </IconsContainer>
                                        {booking.user.role !== 'teacher' && window.growthbook.isOn('goc-virtual-background') ? <IconsContainer>
                                            {isVbPromo && <CustomPopoverWrapper
                                                open={anchorEl}
                                                arrow='bottom-center'
                                                slotProps={{
                                                    paper: {
                                                        sx: {
                                                            width: '390px',
                                                            overflow: 'inherit',
                                                            marginTop: '-23px'
                                                        }
                                                    }
                                                }}
                                            >
                                                <PopoverWrapper>
                                                    <Typography variant='subtitle2'>✨ New! Video Backgrounds for your online class</Typography>
                                                    <Typography variant='body2'>
                                                        Use a video background in your online classes. It helps you stay focused, keeps your space private,
                                                        and shows that you are ready to learn. Choose the right one for you today!
                                                    </Typography>
                                                </PopoverWrapper>
                                            </CustomPopoverWrapper>}
                                            {isVbPromo ? <Badge
                                                badgeContent='New'
                                                color='info'
                                                variant='standard'>
                                                {VbButton}
                                            </Badge> : VbButton}
                                        </IconsContainer> : null}
                                        {featureFlag
                                            ? <>
                                                <Separator orientation='vertical' />
                                                <IconsContainer>
                                                    <SettingIconButton
                                                        data-testid='setting-icon-equipmentPage'
                                                        id='setting-icon-equipmentPage'
                                                        onClick={toggleSettingsModal}
                                                    />
                                                </IconsContainer>
                                            </>
                                            : null}
                                    </ButtonWrapper>
                                    {remainingTime !== void 0 && remainingTime > 0 && booking.user.role === 'student' ? (
                                        <MessageRemainingTimeWrapper>
                                            Your class will start in
                                            {remainingTime !== void 0 && (<>
                                                <br />
                                                <span>
                                                    <Indicator
                                                        seconds={remainingTime}
                                                        isForDiagnosticPage={true}
                                                    />
                                                </span>
                                            </>)}
                                            {remainingTime <= 60 ? 'seconds' : 'minutes'}
                                        </MessageRemainingTimeWrapper>
                                    ) : (
                                        <>
                                            <MatButton
                                                color='primary'
                                                endIcon={<Icon iconName='ArrowForwardRounded' />}
                                                onClick={joinClass}
                                                id='join-class-button'
                                                variant='contained'
                                                size='large'
                                            >
                                                Join Class
                                            </MatButton>
                                        </>
                                    )}
                                </IconButtonContainer>
                                <PrivacyPolicy>
                                    This activity may be recorded for training purposes,
                                    quality control and for your and our staff’s protection, as described in our&nbsp;
                                    <a href={privacyPolicyUrl} target='_blank' rel='noreferrer noopener'>privacy policy</a>
                                </PrivacyPolicy>
                            </ButtonContainer>
                        </>
                    ) : null}
                </InnerWrapper>
                {backgroundSelected.active
                    ? <VBDialog
                        close={() => {
                            void onCamClickHandler()
                        }}
                        selectedBG={backgroundSelected.change}
                        setBackgroundSelected={setBackgroundSelected}
                        selectedCameraId={selectedCameraId}
                    />
                    : null}
                {isMobileSafari
                    ? <IOSPermissionMessageDialog type={permissionType} isOpen={isMessageDialogOpen} toggleIOSPermissionMessageDialog={toggleMessageDialog} />
                    : <MessageDialog type={permissionType} isOpen={isMessageDialogOpen} toggleMessageDialog={toggleMessageDialog} />
                }
                {isSettingActive && <SettingDialog close={toggleSettingsModal} onCameraSelect={handleCameraSelection} selectedCam={selectedCameraId} />}
            </LogoContainer>
        </SessionWrapper>
    )
}

export { DiagnosticsPage }
